import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NavItem } from '../../../../core/models/nav-item.model';
import {
  AccountType,
  AuthenticationService,
} from '../../../../shared/services/auth/authentication.service';
import { LayoutService } from '../../../../shared/services/layout/layout.service';
import {
  catchError,
  combineLatest,
  delay,
  map,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import {
  ApiPaginationResponse,
  ApprovalResponse,
} from '../../../../core/models/api-response.model';
import { ApiService } from '../../../../core/services/api.service';
import { HasAnyPermissionDirective } from '../../../../shared/directives/has-any-permission.directive';
import { IconifyIconComponent } from '../../../../shared/components/iconify-icon/iconify-icon.component';

@Component({
  selector: 'app-sidebar-nav',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    HasAnyPermissionDirective,
    IconifyIconComponent,
  ],
  templateUrl: './sidebar-nav.component.html',
  styleUrl: './sidebar-nav.component.scss',
})
export class SidebarNavComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);

  collapsed = false;
  private expanded = new Set<string>();

  menu: NavItem[] = [];
  totalPendingApprovals: number | null = null;

  private destroy$: Subject<void> = new Subject();

  constructor(
    private readonly apiService: ApiService,
    private readonly authenticationService: AuthenticationService,
    private readonly layoutService: LayoutService,
  ) {}

  get accountType(): AccountType {
    return this.authenticationService.accountType;
  }

  get items(): NavItem[] {
    return this.menu;
  }

  ngOnInit(): void {
    console.log(this.accountType);

    // Filter menu items based on permissions before rendering
    const filterByPermission = (items: NavItem[]): NavItem[] => {
      return items.filter((item) => {
        // Check parent permission
        const hasPermission =
          !item.permissions?.length ||
          this.authenticationService.hasAnyPermission(item.permissions);

        if (!hasPermission) return false;

        // Filter children recursively
        if (item.children?.length) {
          item.children = item.children.filter(
            (child) =>
              !child.permissions?.length ||
              this.authenticationService.hasAnyPermission(child.permissions),
          );
          // Only show parent if it has visible children or no children
          return item.children.length > 0 || !item.children;
        }

        return true;
      });
    };

    if (this.accountType === 'ADMIN') {
      const pendingApprovals$ = timer(0, 60_000).pipe(
        switchMap(() => this.fetchPendingApprovals()),
        catchError(() => of(0)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

      combineLatest({
        menu: this.layoutService.menu,
        pendingApprovals: pendingApprovals$,
      })
        .pipe(
          map(({ menu, pendingApprovals }) => {
            const updatedMenu = menu.map((item) =>
              item.id === 'approval'
                ? { ...item, badgeCount: pendingApprovals }
                : item,
            );
            return filterByPermission(updatedMenu);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe((menu) => {
          this.menu = menu;
        });
    } else if (this.accountType === 'USER') {
      this.layoutService.menu
        .pipe(
          map((menu) => filterByPermission(menu)),
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: (menu) => {
            this.menu = menu;
          },
        });
    }

    this.autoExpandActive();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private fetchPendingApprovals(page = 1) {
    const payload = {
      page,
      limit: 1,
    };

    return this.apiService
      .post<
        ApiPaginationResponse<ApprovalResponse>
      >('approvals/pending/paged', payload)
      .pipe(
        map((x) => {
          if (!x.success) {
            return 0;
          }
          return x.data.total ?? 0;
        }),
        catchError(() => of(0)),
      );
  }

  toggleSidebar(): void {
    this.collapsed = !this.collapsed;
    if (this.collapsed) this.expanded.clear();
  }

  toggle(item: NavItem): void {
    if (this.expanded.has(item.label)) {
      this.expanded.delete(item.label);
    } else {
      this.expanded.add(item.label);
    }
  }

  isExpanded(item: NavItem): boolean {
    return this.expanded.has(item.label);
  }

  private autoExpandActive(): void {
    const url = this.router.url;
    for (const item of this.items) {
      if (item.children?.some((c) => c.route && url.startsWith(c.route))) {
        this.expanded.add(item.label);
      }
    }
  }

  logout() {
    this.authenticationService.logout();
  }
}
